import { ReactNode, useCallback } from 'react';
import {
    FormControl,
    ToggleButtonGroup,
    ToggleButton,
    Radio,
    Checkbox,
    Typography,
    Box,
    FormHelperText,
    SxProps,
    useMediaQuery,
    useTheme,
} from '@mui/material';

export type RadioValue = string | boolean | number;

interface BaseRadioButtonGroupOption {
    label: ReactNode | string;
    description?: ReactNode;
    value: RadioValue;
    disabled?: boolean;
}

interface CommonProps {
    options: BaseRadioButtonGroupOption[];
    name?: string;
    direction?: 'row' | 'column';
    disabled?: boolean;
    error?: boolean;
    errorMessage?: string;
    sx?: SxProps;
}

type BaseRadioButtonGroupProps =
    | (CommonProps & {
          multiple?: false;
          value?: RadioValue;
          onChange?: (value: RadioValue) => void;
      })
    | (CommonProps & {
          multiple: true;
          value?: RadioValue[];
          onChange?: (value: RadioValue[]) => void;
      });

// ---- Per-option content (indicator + label/description) ----
function OptionContent({
    option,
    multiple,
    checked,
    disabled,
}: Readonly<{
    option: BaseRadioButtonGroupOption;
    multiple: boolean;
    checked: boolean;
    disabled: boolean;
}>) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {multiple ? (
                <Checkbox
                    checked={checked}
                    disabled={disabled}
                    disableRipple
                    size="medium"
                    slotProps={{ input: { 'aria-hidden': true, tabIndex: -1 } }}
                    sx={{ marginRight: 1, pointerEvents: 'none', padding: 0.75 }}
                />
            ) : (
                <Radio
                    checked={checked}
                    disabled={disabled}
                    disableRipple
                    size="medium"
                    slotProps={{ input: { 'aria-hidden': true, tabIndex: -1 } }}
                    sx={{ marginRight: 1, pointerEvents: 'none', padding: 0.75 }}
                />
            )}
            <Box>
                <Typography variant="subtitle1" sx={{ color: 'text.primary' }}>
                    {option.label}
                </Typography>
                {option.description && (
                    <Typography variant="body1" sx={{ color: 'text.primary', mt: 0.25 }}>
                        {option.description}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export function BaseRadioButtonGroup({
    options,
    name,
    direction = 'row',
    disabled,
    error,
    errorMessage,
    sx,
    multiple = false,
    value,
    onChange,
}: Readonly<BaseRadioButtonGroupProps>) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleChange = useCallback(
        (_event: React.MouseEvent<HTMLElement>, newValue: RadioValue | RadioValue[] | null) => {
            if (newValue === null) {
                return;
            }
            (onChange as ((v: RadioValue | RadioValue[]) => void) | undefined)?.(newValue);
        },
        [onChange]
    );

    let responsiveOrientation: 'horizontal' | 'vertical';
    if (isMobile) {
        responsiveOrientation = 'vertical';
    } else {
        responsiveOrientation = direction === 'row' ? 'horizontal' : 'vertical';
    }

    const isChecked = (optionValue: RadioValue) => {
        if (multiple) {
            return Array.isArray(value) && value.includes(optionValue);
        }
        return value === optionValue;
    };

    return (
        <FormControl error={error} variant="standard" fullWidth sx={sx}>
            <ToggleButtonGroup
                orientation={responsiveOrientation}
                data-testid={name}
                variant="outlinedControl"
                color={error ? 'error' : 'info'}
                value={value}
                disabled={disabled}
                exclusive={!multiple}
                onChange={handleChange}
                sx={{ rowGap: 2 }}>
                {options.map((option) => (
                    <ToggleButton
                        key={option.value.toString()}
                        name={name}
                        value={option.value}
                        disabled={option.disabled}
                        sx={{ flex: 1 }}>
                        <OptionContent
                            option={option}
                            multiple={multiple}
                            checked={isChecked(option.value)}
                            disabled={Boolean(disabled || option.disabled)}
                        />
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            {error && errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
        </FormControl>
    );
}
